import Repository from './Repository';

class MonitorRepository extends Repository {
	/**
	 * Resolves an array of monitor action group template objects from the API.
	 *
	 * @returns {Promise<array>}
	 */
	async allActionGroupTemplates () {
		const include = [
			'monitors-count',
			'monitor-action-groups.success-action',
			'monitor-action-groups.failure-action',
			'monitor-action-groups.success-action.monitorActionSettings',
			'monitor-action-groups.failure-action.monitorActionSettings',
			'success-actions-count',
			'failure-actions-count'
		];

		return this.getWithToken(
			`/mag-templates?include=${include.join(',')}`
		);
	}

	availableActionGroupTemplates (clientId) {
		const include = [
			'monitors-count',
			'monitor-action-groups.success-action',
			'monitor-action-groups.failure-action',
			'monitor-action-groups.success-action.monitorActionSettings',
			'monitor-action-groups.failure-action.monitorActionSettings',
			'success-actions-count',
			'failure-actions-count'
		];

		return this.getWithToken(`/clients/${clientId}/mag-templates/available?include=${include.join(',')}`);
	}

	/**
	 * Stores a new monitor action group template.
	 *
	 * @param {object} requestData The name of the new template
	 * @returns {Promise<object>}
	 */
	async storeActionGroupTemplate (requestData) {
		return this.postWithToken('/mag-templates', requestData);
	}

	/**
	 * Updates the monitor action group template at the given ID.
	 *
	 * @param {int} id The ID of the monitor action group template to update
	 * @param {object} requestData The new name of the template
	 * @returns {Promise<object>}
	 */
	async updateActionGroupTemplate (id, requestData) {
		return this.putWithToken(`/mag-templates/${id}`, requestData);
	}

	/**
	 * Deletes the monitor action group template at the given ID.
	 *
	 * @param {int} id The ID of the monitor action group template
	 * @returns {Promise}
	 */
	async deleteActionGroupTemplate (id) {
		return this.deleteWithToken(`/mag-templates/${id}`);
	}

	/**
	 * Stores a new action group with the given template and monitor action IDs.
	 *
	 * @param {int} templateId The ID of the related monitor action group template
	 * @param {int} successActionId The ID of the related "success" monitor action
	 * @param {int} failureActionId The ID of the related "failure" monitor action
	 * @returns {Promise<object>}
	 */
	async storeActionGroup (templateId, successActionId, failureActionId) {
		return this.postWithToken(`/mag-templates/${templateId}/action-groups`, {
			success_action_id: successActionId,
			failure_action_id: failureActionId
		});
	}

	/**
	 * Deletes the action group with the given ID and monitor action group template ID.
	 *
	 * @param {int} templateId The ID of the related monitor action group template
	 * @param {int} actionGroupId The ID of the related monitor action group
	 * @returns {Promise}
	 */
	async deleteActionGroup (templateId, actionGroupId) {
		return this.deleteWithToken(`/mag-templates/${templateId}/action-groups/${actionGroupId}`);
	}

	/**
	 * Resolves an array of action types from the API.
	 *
	 * @param clientId
	 * @returns {Promise<array>}
	 */
	async actionTypes (clientId) {
		let clientParam = '';
		if (clientId !== null) {
			clientParam = `?clientId=${clientId}`;
		}

		return this.getWithToken(`/monitor-actions/types${clientParam}`);
	}

	/**
	 * Stores a new monitor action.
	 *
	 * @param {string} type The type of the action to store
	 * @param {object} settings The settings object
	 * @returns {Promise<object>}
	 */
	async storeAction (type, settings) {
		return this.postWithToken('/monitor-actions', {
			type,
			settings
		});
	}

	/**
	 * Stores a new monitor.
	 *
	 * @param {string} type The type of resource that we're storing the monitor on. Can be 'client', 'group', or 'device'.
	 * @param {int} resourceId The ID of the resource to store the monitor on
	 * @param {string} name The monitor name
	 * @param {string} monitorType The monitor type key
	 * @param {string} thresholdCondition The threshold condition
	 * @param {string} timezone The timezone of the monitor
	 * @param {int} failureNotificationThreshold The failure notification threshold
	 * @param {string} cron The cron expression
	 * @param {bool} isActive Flag for whether the monitor is active
	 * @param {int} magTemplateId The monitor action group template ID
	 * @param {object} settings An encapsulated object containing setting values.
	 * @param {bool} useSchedule
	 * @returns {Promise<object>}
	 */
	async storeMonitor (
		type,
		resourceId,
		name,
		monitorType,
		thresholdCondition,
		timezone,
		failureNotificationThreshold,
		cron,
		isActive,
		magTemplateId,
		settings,
		useSchedule
	) {
		return this.postWithToken('/monitors', {
			monitored_type: type,
			monitored_id: resourceId,
			name,
			type: monitorType,
			threshold_condition: thresholdCondition,
			timezone,
			cron,
			is_active: isActive,
			monitor_action_group_template_id: magTemplateId,
			failure_notification_threshold: failureNotificationThreshold,
			use_schedule: useSchedule,
			settings
		});
	}

	/**
	 * Updates an existing monitor.
	 *
	 * @param {int} monitorId The monitor ID to update
	 * @param {string} type The type of resource that we're updating the monitor on. Can be 'client', 'group', or 'device'.
	 * @param {int} resourceId The ID of the resource the monitor is assigned to
	 * @param {string} name The monitor name
	 * @param {string} monitorType The monitor type key
	 * @param {string} thresholdCondition The threshold condition
	 * @param {string} timezone The timezone of the monitor
	 * @param {int} failureNotificationThreshold The failure notification threshold
	 * @param {string} cron The cron expression
	 * @param {bool} isActive Flag for whether the monitor is active
	 * @param {int} magTemplateId The monitor action group template ID
	 * @param {object} settings An encapsulated object containing setting values.
	 * @returns {Promise<object>}
	 */
	async updateMonitor (
		monitorId,
		type,
		resourceId,
		name,
		monitorType,
		thresholdCondition,
		timezone,
		failureNotificationThreshold,
		cron,
		isActive,
		magTemplateId,
		settings
	) {
		return this.putWithToken(`/monitors/${monitorId}`, {
			monitored_type: type,
			monitored_id: resourceId,
			name,
			type: monitorType,
			threshold_condition: thresholdCondition,
			timezone,
			cron,
			is_active: isActive,
			monitor_action_group_template_id: magTemplateId,
			failure_notification_threshold: failureNotificationThreshold,
			settings
		});
	}

	/**
	 * Overrides an existing monitor.
	 *
	 * @param {int} monitorId The monitor ID to override
	 * @param {string} type The type of the resource that is overriding the monitor. Can be 'client', 'group', or 'device'.
	 * @param {int} resourceId The ID of the resouce that is overriding the monitor.
	 * @returns {Promise<object>} The new monitor created from the existing one.
	 */
	async overrideMonitor (monitorId, type, resourceId) {
		return this.postWithToken(`/monitors/${monitorId}/override`, {
			monitored_type: type,
			monitored_id: resourceId
		});
	}

	/**
	 * Deletes the monitor with the given ID.
	 *
	 * @param {int} monitorId The monitor ID to delete
	 * @returns {Promise}
	 */
	async deleteMonitor (monitorId) {
		return this.deleteWithToken(`/monitors/${monitorId}`);
	}

	/**
	 * Activates the monitor with the given ID.
	 *
	 * @param {int} monitorId The monitor ID to activate
	 * @returns {Promise<object>}
	 */
	async activateMonitor (monitorId) {
		return this.postWithToken(`/monitors/${monitorId}/activate`);
	}

	/**
	 * Deactivates the monitor with the given ID.
	 *
	 * @param {int} monitorId The monitor ID to deactivate
	 * @returns {Promise<object>}
	 */
	async deactivateMonitor (monitorId) {
		return this.postWithToken(`/monitors/${monitorId}/deactivate`);
	}

	/**
	 * Activates the inherited monitor with the given ID.
	 *
	 * @param {int} monitorId The inherited monitor ID to activate
	 * @param {string} monitoredType The type of resource that's inheriting the monitor. Can be 'group' or 'device'
	 * @param {int} monitoredId The ID of the resource that's inheriting the monitor.
	 * @returns {Promise<object>}
	 */
	async activateInheritedMonitor (monitorId, monitoredType, monitoredId) {
		return this.postWithToken(`/monitors/${monitorId}/activate`, {
			monitored_type: monitoredType,
			monitored_id: monitoredId
		});
	}

	/**
	 * Deactivates the inherited monitor with the given ID.
	 *
	 * @param {int} monitorId The inherited monitor ID to deactivate
	 * @param {string} monitoredType The type of resource that's inheriting the monitor. Can be 'group' or 'device'
	 * @param {int} monitoredId The ID of the resource that's inheriting the monitor.
	 * @returns {Promise<object>}
	 */
	async deactivateInheritedMonitor (monitorId, monitoredType, monitoredId) {
		return this.postWithToken(`/monitors/${monitorId}/deactivate`, {
			monitored_type: monitoredType,
			monitored_id: monitoredId
		});
	}

	/**
	 * Get additional details of a monitor scoped to the query string
	 *
	 * @param {int} id id of the monitor
	 * @param {string} queryString the level and id of the item(s) we will get details for
	 */
	getDetails (id, queryString) {
		return this.getWithToken(`/monitors/${id}/details${queryString}`);
	}

	/**
	 * Run a monitor on command
	 *
	 * @param {int} id id of the monitor
	 * @param {object} request contains the type and id of the model to run the monitor for
	 */
	runMonitor (id, request) {
		return this.getWithToken(`/monitors/${id}/run?type=${request.type}&id=${request.id}`);
	}

	getActionGroupTemplate (id) {
		return this.getWithToken(`/mag-templates/${id}`);
	}

	testActions (id, request) {
		return this.postWithToken(`/monitors/${id}/test-actions`, request);
	}
}

export default new MonitorRepository();
